import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { detectSalesOpportunities } from "../../services/apiGpt";
import { i18n } from "../../translate/i18n";
import { getGPTTranslation, mapI18nToGPTLanguage } from "../../utils/gptTranslations";

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 600,
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  opportunityCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.success.main}`,
    backgroundColor: theme.palette.background.default,
  },
  missedCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.background.default,
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  errorContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ef9a9a",
  },
  sentimentChip: {
    marginLeft: theme.spacing(2),
  },
  priorityHigh: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  priorityMedium: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  priorityLow: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
}));

const SalesOpportunitiesModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // Get current language for translations
  const currentLang = mapI18nToGPTLanguage(i18n.language);
  const t = (key, params) => getGPTTranslation(key, currentLang, params);

  useEffect(() => {
    if (open && ticketId) {
      loadOpportunities();
    } else if (!open) {
      setData(null);
      setError(null);
    }
  }, [open, ticketId]);

  const loadOpportunities = async () => {
    if (!ticketId) {
      setError(i18n.t("gptModals.noTicketSelected") || "No ticket selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const systemLang = i18n.language || localStorage.getItem("language") || "en";
      const result = await detectSalesOpportunities(ticketId, systemLang);
      setData(result);
    } catch (err) {
      console.error("[SalesOpportunitiesModal] Error:", err);
      setError(err.response?.data?.error || err.message || "Failed to detect opportunities");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return classes.priorityHigh;
      case "medium":
        return classes.priorityMedium;
      default:
        return classes.priorityLow;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "primary";
      case "negative":
        return "secondary";
      default:
        return "default";
    }
  };

  const translateSentiment = (sentiment) => {
    if (!sentiment) return "N/A";
    const key = sentiment.toLowerCase();
    if (key === "positive") return t("positive");
    if (key === "negative") return t("negative");
    if (key === "neutral") return t("neutral");
    return sentiment;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <MonetizationOnIcon color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>💰 {t("salesOpportunities")}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {t("analyzingSalesOpportunities")}
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorContainer}>
            <Typography variant="body2">
              <strong>Error:</strong> {error}
            </Typography>
          </Box>
        ) : data ? (
          <>
            {/* Overall Sentiment */}
            <Box mb={3} display="flex" alignItems="center">
              <Typography variant="subtitle1">{t("sentiment", { sentiment: "" }).replace(": ", ":")}:</Typography>
              <Chip
                label={translateSentiment(data.overallSentiment)}
                color={getSentimentColor(data.overallSentiment)}
                className={classes.sentimentChip}
              />
            </Box>

            {/* Conversation Summary */}
            {data.conversationSummary && (
              <Box className={classes.section}>
                <Typography variant="h6" gutterBottom>
                  📋 {t("conversationSummary")}
                </Typography>
                <Paper className={classes.opportunityCard} elevation={0}>
                  <Typography variant="body2">{data.conversationSummary}</Typography>
                </Paper>
              </Box>
            )}

            {/* Detected Opportunities */}
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
                {t("opportunitiesDetected")} ({data.opportunities?.length || 0})
              </Typography>
              {data.opportunities && data.opportunities.length > 0 ? (
                data.opportunities.map((opp, index) => (
                  <Paper key={index} className={classes.opportunityCard} elevation={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Typography variant="subtitle1" gutterBottom>
                        {opp.product || opp.opportunity}
                      </Typography>
                      <Box className={classes.chipContainer}>
                        {opp.priority && (
                          <Chip
                            label={opp.priority}
                            size="small"
                            className={getPriorityClass(opp.priority)}
                          />
                        )}
                        {opp.confidence && (
                          <Chip
                            label={t("confidence", { percent: Math.round(opp.confidence * 100) })}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                    {opp.reason && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {opp.reason}
                      </Typography>
                    )}
                    {opp.suggestedAction && (
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon fontSize="small" color="primary" style={{ marginRight: 8 }} />
                        <Typography variant="body2" color="primary">
                          {opp.suggestedAction}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t("noOpportunitiesFound")}
                </Typography>
              )}
            </Box>

            {/* Missed Opportunities */}
            {data.missedOpportunities && data.missedOpportunities.length > 0 && (
              <Box className={classes.section}>
                <Typography variant="h6" gutterBottom>
                  <WarningIcon style={{ verticalAlign: "middle", marginRight: 8, color: "#ff9800" }} />
                  {t("opportunitiesMissed")} ({data.missedOpportunities.length})
                </Typography>
                {data.missedOpportunities.map((missed, index) => (
                  <Paper key={index} className={classes.missedCard} elevation={1}>
                    <Typography variant="subtitle1" gutterBottom>
                      {missed.opportunity}
                    </Typography>
                    {missed.reason && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {missed.reason}
                      </Typography>
                    )}
                    {missed.recommendation && (
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" color="primary">
                          💡 {missed.recommendation}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box className={classes.loadingContainer}>
            <Typography variant="body2" color="textSecondary">
              {t("analyzingSalesOpportunities")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={loadOpportunities}
              style={{ marginTop: 16 }}
            >
              {t("analyze")}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {data && (
          <Button onClick={loadOpportunities} color="primary" disabled={loading}>
            {t("refresh")}
          </Button>
        )}
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOpportunitiesModal;
